import React from 'react';
import { Box, Button, Card, CardContent, Stack, Typography } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import {
  buildSafetyCaseHomeStatus,
  type SafetyCaseHomeStatusFacts,
} from '../../domain/safetyCases/safetyCaseHomeStatus';

function Fact({
  label,
  value,
  href,
  muted,
  testId,
}: {
  label: string;
  value: string;
  href?: string | null;
  muted?: boolean;
  testId: string;
}) {
  return (
    <Box data-testid={testId}>
      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700, letterSpacing: 0.04 }}>
        {label}
      </Typography>
      {href ? (
        <Typography
          component={RouterLink}
          to={href}
          variant="body2"
          sx={{ display: 'block', fontWeight: 700, textDecoration: 'none', color: 'primary.main' }}
        >
          {value}
        </Typography>
      ) : (
        <Typography variant="body2" sx={{ fontWeight: muted ? 500 : 700 }} color={muted ? 'text.secondary' : 'text.primary'}>
          {value}
        </Typography>
      )}
    </Box>
  );
}

export function SafetyCaseHomeStatusStrip({
  facts,
  onPrimaryCta,
}: {
  facts: SafetyCaseHomeStatusFacts;
  onPrimaryCta: () => void;
}) {
  const status = buildSafetyCaseHomeStatus(facts);
  return (
    <Card variant="outlined" sx={{ borderRadius: 3 }} data-testid="safety-case-home-status">
      <CardContent>
        <Typography variant="overline" sx={{ fontWeight: 800, letterSpacing: 0.12 }}>
          Current status
        </Typography>
        {typeof facts.scanCount === 'number' && facts.scanCount === 0 ? (
          <Typography variant="body2" sx={{ fontWeight: 700, mt: 0.5 }} data-testid="safety-case-measurement-active">
            Measurement is active
          </Typography>
        ) : null}
        {typeof facts.scanCount === 'number' && facts.scanCount > 1 ? (
          <Typography variant="body2" sx={{ fontWeight: 700, mt: 0.5 }} data-testid="safety-case-current-scan-headline">
            Current Scan is the management headline. First Scan is historical.
          </Typography>
        ) : null}
        <Box
          sx={{
            mt: 1.25,
            display: 'grid',
            gap: 1.5,
            gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(4, minmax(0, 1fr))' },
          }}
        >
          <Fact
            label={typeof facts.scanCount === 'number' && facts.scanCount > 1 ? 'Current Scan' : 'Current Scan'}
            value={status.currentScanLabel}
            href={status.currentScanHref}
            muted={status.currentScanEmpty}
            testId="safety-case-home-current-scan"
          />
          <Fact
            label="Latest Signed"
            value={status.latestSignedLabel}
            href={status.latestSignedHref}
            muted={status.latestSignedEmpty}
            testId="safety-case-home-latest-signed"
          />
          <Fact
            label="Control"
            value={status.controlLabel}
            muted={status.controlEmpty}
            testId="safety-case-home-control"
          />
          <Fact
            label="New activity"
            value={status.newActivityLabel}
            testId="safety-case-home-new-activity"
          />
        </Box>
        {status.currentScanEmpty && (status.latestRunLabel || status.scanOutcomeLabel || status.scanOutcomeReason) ? (
          <Box
            sx={{
              mt: 1.25,
              display: 'grid',
              gap: 1.5,
              gridTemplateColumns: { xs: '1fr', md: 'repeat(3, minmax(0, 1fr))' },
            }}
            data-testid="safety-case-home-current-scan-reason"
          >
            {status.latestRunLabel ? (
              <Fact
                label="Latest scheduled run"
                value={`${status.latestRunLabel}${status.latestRunStatus ? ` · ${status.latestRunStatus}` : ''}`}
                muted
                testId="safety-case-home-latest-run"
              />
            ) : null}
            {status.scanOutcomeLabel ? (
              <Fact
                label="Safety Scan outcome"
                value={status.scanOutcomeLabel}
                muted
                testId="safety-case-home-scan-outcome"
              />
            ) : null}
            {status.scanCreatedHref && !status.currentScanHref ? (
              <Box data-testid="safety-case-home-open-created-scan">
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700, letterSpacing: 0.04 }}>
                  Created Scan
                </Typography>
                <Typography
                  component={RouterLink}
                  to={status.scanCreatedHref}
                  variant="body2"
                  sx={{ display: 'block', fontWeight: 700, textDecoration: 'none', color: 'primary.main' }}
                >
                  Open Safety Scan
                </Typography>
              </Box>
            ) : null}
            {status.scanOutcomeReason ? (
              <Fact
                label="Reason"
                value={status.scanOutcomeReason}
                muted
                testId="safety-case-home-scan-outcome-reason"
              />
            ) : null}
          </Box>
        ) : null}
        {(status.nextScheduledLabel || status.scanCountLabel || status.controlOwnerLabel) ? (
          <Box
            sx={{
              mt: 1.25,
              display: 'grid',
              gap: 1.5,
              gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(3, minmax(0, 1fr))' },
            }}
          >
            {status.nextScheduledLabel ? (
              <Fact
                label="Next scheduled measurement"
                value={status.nextScheduledLabel}
                muted
                testId="safety-case-home-next-scheduled"
              />
            ) : null}
            {status.scanCountLabel ? (
              <Fact
                label="Scans"
                value={status.scanCountLabel}
                testId="safety-case-home-scan-count"
              />
            ) : null}
            {status.controlOwnerLabel ? (
              <Fact
                label="Control owner"
                value={status.controlOwnerLabel}
                testId="safety-case-home-control-owner"
              />
            ) : null}
          </Box>
        ) : null}
        <Stack
          direction="row"
          spacing={1}
          sx={{ mt: 1.75 }}
          data-testid={status.primaryCtaIsAction ? 'safety-case-dominant-cta' : 'safety-case-home-primary-cta'}
        >
          {status.primaryCtaIsAction ? (
            <Button
              variant="contained"
              onClick={onPrimaryCta}
              data-testid={
                facts.primaryCtaKind === 'verify_observations'
                  ? 'verify-observations-cta'
                  : 'safety-case-home-primary-cta'
              }
              sx={{ textTransform: 'none', fontWeight: 700 }}
            >
              {status.primaryCtaLabel}
            </Button>
          ) : (
            <Typography variant="body2" color="text.secondary">
              {status.primaryCtaLabel}
            </Typography>
          )}
        </Stack>
      </CardContent>
    </Card>
  );
}
